import AccessTime from '@mui/icons-material/AccessTime'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import VideocamIcon from '@mui/icons-material/Videocam'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { Box, Button, Typography, IconButton, Chip, TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import { styled } from '@mui/material/styles'
import dayjs from 'dayjs'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { RectangleRadioButton } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents'
import { MOMENT_FORMATS, BOOKED_EVENT_MODES, displayNamesOfEventTypes } from 'src/consumer/constants'
import { getValidNumberFromStringNumber } from 'src/consumer/helpers/utilFunctions'
import { getDataWithoutParams, cancelBookedSession } from 'src/consumer/services/featureServices'
import { getBookedSlotDetails } from 'src/mentor/services/bookedSlotCache'
import { getDataWithParams } from 'src/consumer/services/profileService'
import { isCommonResponseSuccessfulV2, getDataMapFromCommonResponse, isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { postMPDataWithoutParams } from 'src/mentor/services'
import { toast } from 'react-toastify'
import { usePromiseDialog } from 'src/customHooks/usePromiseDialog'
import DayjsDatePicker from 'src/mentor/components/commonReportComponents/components/datePicker/DayjsDatePicker'
import {
  calculateNextVisitDaysFromDateDayjs,
} from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageComponents'
import { getNextVisitDateDifference, formatTimeSlotInClinicTimezone, getClinicTimezone, getOffDayWeekdaysFromSchedules } from 'src/mentor/components/commonReportComponents/utils/nextVisitRosterCalendarUtils'
import NextVisitSlotSelector from './NextVisitSlotSelector'

// Modern styled number input component
const ModernNumberInput = styled(TextField)(({ theme, error }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    border: `1px solid ${error ? 'rgba(239, 68, 68, 0.4)' : 'rgba(139, 92, 246, 0.25)'}`,
    fontSize: '14px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: '#6b21a8',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: error ? 'rgba(239, 68, 68, 0.6)' : 'rgba(139, 92, 246, 0.4)',
    },
    '&.Mui-focused': {
      borderColor: error ? 'rgba(239, 68, 68, 0.8)' : 'rgba(139, 92, 246, 0.6)',
      boxShadow: error 
        ? '0 0 0 3px rgba(239, 68, 68, 0.15)' 
        : '0 0 0 3px rgba(139, 92, 246, 0.15)',
    },
    '& fieldset': {
      border: 'none',
    },
  },
  '& .MuiInputBase-input': {
    padding: '10px 14px',
    color: '#6b21a8',
    fontWeight: 500,
    '&::placeholder': {
      color: 'rgba(139, 92, 246, 0.5)',
      opacity: 1,
    },
  },
  '& .MuiInputAdornment-root': {
    color: 'rgba(139, 92, 246, 0.6)',
  },
}))

const EMRNextVisitComponentModernized = forwardRef(
  (
    {
      nextVisitInputType,
      setNextVisitInputType,
      nextVisitInputValue,
      setNextVisitInputValue,
      bookedEventInfoStartTime,
      nextVisitBookedEventId,
      showHoldSlotForNextVisit = false,
      currentClinicLocation,
      mentorProfileData,
      roomId,
      currentEventTypeId,
      currentPatient,
      refreshCurrentBookedEventInfoData,
      currentFee,
      currentCurrency,
      currentBookedEventId,
      saveReport,
      nextVisitHoldSlotOmissionReason,
      setNextVisitHoldSlotOmissionReason,
      isMarkingAsCompleted,
      leaveRoom,
    },
    componentRef,
  ) => {
    const [slotSelectorOpen, setSlotSelectorOpen] = useState(false)
    const [bookedSlot, setBookedSlot] = useState(null)
    const [loading, setLoading] = useState(false)
    const [inputError, setInputError] = useState('')
    const [inputTouched, setInputTouched] = useState(false)
    const [bookedSlots, setBookedSlots] = useState([]) // Booked slots for date range
    const [bookedSlotsLoading, setBookedSlotsLoading] = useState(false)
    const [cancelling, setCancelling] = useState(false) // Loading state for cancel operation
    const [rescheduling, setRescheduling] = useState(false) // Loading state for reschedule operation
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false) // Dialog for cancel reason
    const [cancelReason, setCancelReason] = useState('') // Reason for cancellation
    const [refundType, setRefundType] = useState('none') // Refund type: 'none' or 'full'
    const [pendingCancelAction, setPendingCancelAction] = useState(null) // 'cancel' or 'change'
    const holdSlotOmissionDialog = usePromiseDialog()

    const selectedDate = useMemo(() => {
      if (!bookedEventInfoStartTime) {
        return dayjs().add(7, 'days').startOf('day')
      }
      
      if (!nextVisitInputValue || nextVisitInputValue <= 0) {
        // Return a default date (7 days from now) if no value is set
        return dayjs.utc(bookedEventInfoStartTime).local().add(7, 'days').startOf('day')
      }
      
      // Calculate the date directly using dayjs (same approach as datePickerButtonText)
      const numValue = Number(nextVisitInputValue)
      if (isNaN(numValue) || numValue <= 0) {
        return dayjs.utc(bookedEventInfoStartTime).local().add(7, 'days').startOf('day')
      }
      
      const baseDate = dayjs.utc(bookedEventInfoStartTime).local()
      let calculatedDate = baseDate
      
      if (nextVisitInputType === 'days') {
        calculatedDate = baseDate.add(numValue, 'days')
      } else if (nextVisitInputType === 'weeks') {
        calculatedDate = baseDate.add(numValue, 'weeks')
      } else if (nextVisitInputType === 'months') {
        calculatedDate = baseDate.add(numValue, 'months')
      }
      
      return calculatedDate.startOf('day')
    }, [nextVisitInputValue, nextVisitInputType, bookedEventInfoStartTime])

    // Calculate the formatted date string for the date picker button
    const datePickerButtonText = useMemo(() => {
      // Handle empty, null, undefined, or zero values
      if (!nextVisitInputValue || nextVisitInputValue === '' || nextVisitInputValue === 0) {
        return null // Will use placeholder
      }
      
      // Ensure we have a valid start time
      if (!bookedEventInfoStartTime) {
        return null // Will use placeholder
      }
      
      // Convert to number if it's a string
      const numValue = Number(nextVisitInputValue)
      
      // Check if conversion was successful and value is positive
      if (isNaN(numValue) || numValue <= 0) {
        return null // Will use placeholder
      }
      
      try {
        const baseDate = dayjs.utc(bookedEventInfoStartTime).local()
        // Calculate the date directly using dayjs
        let calculatedDate = baseDate
        if (nextVisitInputType === 'days') {
          calculatedDate = baseDate.add(numValue, 'days')
        } else if (nextVisitInputType === 'weeks') {
          calculatedDate = baseDate.add(numValue, 'weeks')
        } else if (nextVisitInputType === 'months') {
          calculatedDate = baseDate.add(numValue, 'months')
        }
        
        return calculatedDate.format('DD MMM YYYY, dddd')
      } catch (error) {
        console.error('Error calculating date picker button text:', error)
        return null // Will use placeholder
      }
    }, [nextVisitInputValue, nextVisitInputType, bookedEventInfoStartTime])

    // Calculate button text for "Hold Slot" button
    const holdSlotButtonText = useMemo(() => {
      if (!selectedDate || !showHoldSlotForNextVisit || !nextVisitInputValue || nextVisitInputValue <= 0) {
        return null
      }
      const formattedDate = selectedDate.format('DD MMM YYYY')
      const dayName = selectedDate.format('dddd')
      return `Hold a specific time for ${formattedDate}, ${dayName}`
    }, [selectedDate, showHoldSlotForNextVisit, nextVisitInputValue])
    
    const currentEventTypeObject = useMemo(() => {
      return mentorProfileData?.eventTypes?.find(
        (eventType) => eventType?.uid === currentEventTypeId,
      )
    }, [mentorProfileData, currentEventTypeId])

    // Weekdays the practitioner doesn't work — painted red in the date picker (still selectable).
    const offDayWeekdays = useMemo(
      () => getOffDayWeekdaysFromSchedules(mentorProfileData, currentEventTypeId),
      [mentorProfileData, currentEventTypeId],
    )

    const dayjsMinDateInLocalTimezone = dayjs.utc(bookedEventInfoStartTime).local()
    const minDate = dayjsMinDateInLocalTimezone.toDate()
    minDate.setHours(0, 0, 0, 0)

    const todayDate = new Date()
    const maxDate = new Date()
    maxDate?.setMonth(todayDate?.getMonth() + 12)

    // Validation function
    const validateInput = (value, inputType) => {
      if (value === '' || value === null || value === undefined) {
        return { isValid: false, error: '' } // Empty is allowed (user can clear)
      }

      const numValue = Number(value)
      
      if (isNaN(numValue)) {
        return { isValid: false, error: 'Please enter a valid number' }
      }

      if (numValue < 0) {
        return { isValid: false, error: 'Cannot be negative' }
      }

      if (numValue === 0) {
        return { isValid: false, error: 'Must be greater than 0' }
      }

      // Set maximum limits based on input type
      const maxLimits = {
        days: 365,
        weeks: 52,
        months: 12,
      }

      const maxLimit = maxLimits[inputType] || 365
      if (numValue > maxLimit) {
        return { 
          isValid: false, 
          error: `Maximum ${maxLimit} ${inputType} allowed` 
        }
      }

      // Check for decimal values (only allow integers)
      if (numValue % 1 !== 0) {
        return { isValid: false, error: 'Please enter a whole number' }
      }

      return { isValid: true, error: '' }
    }

    // Handle input change with validation
    const handleInputChange = (e) => {
      const inputValue = e.target.value
      setInputTouched(true)

      // Allow empty input (user might be clearing)
      if (inputValue === '' || inputValue === null) {
        setNextVisitInputValue('')
        setInputError('')
        return
      }

      // Handle negative sign or invalid characters early
      if (inputValue === '-' || inputValue === '.') {
        // Don't update value, just show placeholder
        return
      }

      const numValue = getValidNumberFromStringNumber(inputValue)
      
      // If completely invalid input, don't update value but show error
      if (inputValue !== '' && (isNaN(numValue) || numValue < 0)) {
        setInputError('Please enter a valid positive number')
        return
      }

      // Update value
      setNextVisitInputValue(numValue)

      // Validate
      const validation = validateInput(numValue, nextVisitInputType)
      setInputError(validation.error)
    }

    // Validate when input type changes
    useEffect(() => {
      if (inputTouched && nextVisitInputValue !== null && nextVisitInputValue !== undefined && nextVisitInputValue !== '') {
        const validation = validateInput(nextVisitInputValue, nextVisitInputType)
        setInputError(validation.error)
      }
    }, [nextVisitInputType])

    function resetNextVisit() {
      setBookedSlot(null)
      setNextVisitInputValue('')
      setNextVisitInputType('days')
    }

    // Reschedule the booked slot using mentor reschedule API
    async function performReschedule(newStartTime, reason, forceReschedule = false) {
      if (!bookedSlot?.id) {
        toast.error('No booking found to reschedule')
        return false
      }

      if (!newStartTime) {
        toast.error('New start time is required')
        return false
      }

      setRescheduling(true)
      try {
        // Convert newStartTime to UTC ISO-8601 format
        const newStartTimeUTC = dayjs(newStartTime).utc().toISOString()
        
        const payload = {
          bookedEventId: bookedSlot.id,
          newStartTime: newStartTimeUTC,
          notifyPatient: true,
          reason: reason?.trim() || 'Rescheduling appointment',
          forceReschedule: forceReschedule,
          reschedulePolicy: forceReschedule ? 'BOOK_IF_CONFLICT' : 'DONT_BOOK_IF_CONFLICT',
        }

        const response = await postMPDataWithoutParams(
          '/api/secure/mentor/booked-events/reschedule',
          payload
        )

        if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
          toast.success('Booking rescheduled successfully')
          
          // Update the booked slot with new data from response
          if (response?.data?.data?.bookedEventInfo) {
            setBookedSlot(response.data.data.bookedEventInfo)
          }
          
          // Refresh the booked event info if callback is available
          if (refreshCurrentBookedEventInfoData) {
            refreshCurrentBookedEventInfoData()
          }
          
          return true
        } else {
          // Handle specific error codes
          const errorCode = response?.data?.code
          let errorMessage = response?.data?.message || 'Failed to reschedule booking'
          
          if (errorCode === 4443) {
            errorMessage = 'Booking already marked completed. Rescheduling is not permitted.'
          } else if (errorCode === 4444) {
            errorMessage = 'Requested time is in the past. Please select a future time.'
          } else if (errorCode === 4448 || errorCode === 4449) {
            errorMessage = 'Time slot conflicts with another booking. Please select a different time or use force reschedule.'
          } else if (errorCode === 48980) {
            errorMessage = 'Self-reschedule is disabled for this clinic. Please contact support.'
          }
          
          toast.error(errorMessage)
          return false
        }
      } catch (error) {
        console.error('Error rescheduling booking:', error)
        toast.error('Failed to reschedule booking. Please try again.')
        return false
      } finally {
        setRescheduling(false)
      }
    }

    const handleSlotSelection = async (slotData) => {
      // If there's an existing booked slot with status "booked", use reschedule API
      if (bookedSlot?.id && bookedSlot?.status === 'booked') {
        const newStartTime = slotData.startDateTime || slotData.startTime
        const reason = 'Rescheduling to new time slot'
        const success = await performReschedule(newStartTime, reason, false)
        
        if (success) {
          // Update booked slot with new data
          const selectedDateTime = dayjs(newStartTime)
          setSelectedNextVisitDate(selectedDateTime.startOf('day'))
        }
        // If reschedule fails, don't update the slot
      } else {
        // Normal flow - set the new booked slot
        setBookedSlot(slotData)
        const selectedDateTime = dayjs(slotData.startDateTime || slotData.startTime)
        setSelectedNextVisitDate(selectedDateTime.startOf('day'))
      }
    }

    // Generate meaningful cancellation reason
    function generateCancellationReason(slot, action = 'cancel') {
      if (!slot) return 'Slot cancellation'
      
      const slotDate = slot.startTime || slot.startDateTime
      let reason = ''
      
      if (action === 'change') {
        reason = 'Changing to a different time slot'
      } else {
        reason = 'Slot cancellation'
      }
      
      // Add slot details if available
      if (slotDate) {
        try {
          const date = dayjs(slotDate)
          if (date.isValid()) {
            const dateStr = date.format('DD MMM YYYY')
            const timeStr = slot.timeSlot || (() => {
              try {
                const start = dayjs(slot.startTime || slot.startDateTime)
                const end = slot.endTime || slot.endDateTime
                  ? dayjs(slot.endTime || slot.endDateTime)
                  : start.add(slot.eventDuration || 30, 'minutes')
                return `${start.format('h:mm A')} - ${end.format('h:mm A')}`
              } catch {
                return ''
              }
            })()
            
            if (timeStr) {
              reason += ` for ${dateStr} at ${timeStr}`
            } else {
              reason += ` for ${dateStr}`
            }
          }
        } catch {
          // Ignore date formatting errors
        }
      }
      
      return reason || 'Slot cancellation'
    }

    // Perform the actual cancellation
    async function performCancel(reason, refundTypeToUse = 'none') {
      if (!bookedSlot?.id) {
        toast.error('No booking found to cancel')
        return false
      }

      setCancelling(true)
      // Capture the action before it's cleared in finally block
      const cancelAction = pendingCancelAction || 'cancel'
      const wasChangingSlot = cancelAction === 'change'
      
      try {
        // Auto-generate meaningful reason if not provided
        let reasonToSend = reason?.trim()
        if (!reasonToSend) {
          reasonToSend = generateCancellationReason(bookedSlot, cancelAction)
        }
        
        const response = await cancelBookedSession(
          '/api/secure/teacher/booked-slot/cancel',
          bookedSlot.id,
          reasonToSend,
          refundTypeToUse, // refundType
          bookedSlot?.slug // bookedEventSlug
        )

        if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
          toast.success('Booking cancelled successfully')
          
          if (cancelAction === 'cancel') {
            // Just cancel - reset next visit
            resetNextVisit()
          } else if (wasChangingSlot) {
            // Cancel and prepare to open selector for new booking
            setBookedSlot(null)
          }
          
          // Refresh the booked event info if callback is available
          // This will update nextVisitBookedEventId to null, removing blocked session details
          if (refreshCurrentBookedEventInfoData) {
            await refreshCurrentBookedEventInfoData()
          }
          
          if (saveReport && wasChangingSlot) {
            try {
              await saveReport({
                mode: 'draft',
                hideToast: true,
                refreshBookedEvent: false,
              })
            } catch (error) {
              console.error('Error auto-saving report after slot cancellation:', error)
            }
          }
          
          // Open slot selector after state updates and auto-save
          if (wasChangingSlot) {
            // Use setTimeout to ensure state updates have propagated and dialog is closed
            setTimeout(() => {
              setSlotSelectorOpen(true)
            }, 200)
          }
          
          return true
        } else {
          toast.error(response?.data?.message || 'Failed to cancel booking')
          return false
        }
      } catch (error) {
        console.error('Error cancelling booking:', error)
        toast.error('Failed to cancel booking. Please try again.')
        return false
      } finally {
        setCancelling(false)
        setCancelDialogOpen(false)
        setCancelReason('')
        setRefundType('none')
        setPendingCancelAction(null)
      }
    }

    // Cancel the booked slot - opens dialog for reason
    function handleCancelSlot() {
      if (!bookedSlot?.id) {
        toast.error('No booking found to cancel')
        return
      }

      // Check if status is "booked" - cannot cancel, must use reschedule
      if (bookedSlot?.status === 'booked') {
        toast.error('Cannot cancel a booked appointment. Please use "Change Slot" to reschedule it.')
        return
      }

      setPendingCancelAction('cancel')
      // Pre-fill the reason with auto-generated meaningful reason
      const autoReason = generateCancellationReason(bookedSlot, 'cancel')
      setCancelReason(autoReason)
      setCancelDialogOpen(true)
    }

    // Handle change slot - reschedule if booked, otherwise cancel and book new
    function handleChangeSlot() {
      if (!bookedSlot?.id) {
        // If no existing booking, just open the selector
        setSlotSelectorOpen(true)
        return
      }

      // If status is "booked", we'll use reschedule API when user selects new slot
      // Otherwise, cancel and book new (skip dialog, auto-fill reason)
      if (bookedSlot?.status === 'booked') {
        // Open selector - reschedule will be called when slot is selected
        setSlotSelectorOpen(true)
      } else {
        // For blocked slots, cancel directly with auto-filled reason (no dialog)
        setPendingCancelAction('change')
        const autoReason = generateCancellationReason(bookedSlot, 'change')
        performCancel(autoReason, 'none')
      }
    }

    // Handle confirm cancel from dialog
    function handleConfirmCancel() {
      performCancel(cancelReason, refundType)
    }

    function setSelectedNextVisitDate(nextValue) {
      const differenceInDays = calculateNextVisitDaysFromDateDayjs(
        dayjs(nextValue).format(MOMENT_FORMATS.formatThatServerSends),
        dayjs.utc(bookedEventInfoStartTime).local(),
      )
      setNextVisitInputType('days')
      setNextVisitInputValue(differenceInDays)
      // Update selectedDate to match the chosen date so slot selector preselects it
      const chosenDate = dayjs(nextValue).startOf('day')
      // If hold slot is enabled, open the selector with the preselected date
      if (showHoldSlotForNextVisit && !bookedSlot) {
        // Use a small delay to ensure state is updated
        setTimeout(() => {
          setSlotSelectorOpen(true)
        }, 100)
      }
    }

    useEffect(() => {
      if (!nextVisitBookedEventId) return
      let cancelled = false
      setLoading(true)
      getBookedSlotDetails(nextVisitBookedEventId).then((slot) => {
        if (cancelled) return
        if (slot) setBookedSlot(slot)
        setLoading(false)
      })
      return () => {
        cancelled = true
      }
    }, [nextVisitBookedEventId])

    // Fetch booked slots when date changes (if hold slot is enabled)
    useEffect(() => {
      const fetchBookedSlots = async () => {
        if (!showHoldSlotForNextVisit || !mentorProfileData?.id || !currentEventTypeObject || !selectedDate) {
          return
        }

        setBookedSlotsLoading(true)
        try {
          // Calculate date range with 20 days buffer
          // The popup shows slots for a range, so we need to fetch booked slots for that range
          const startDate = selectedDate.subtract(20, 'days').startOf('day')
          const endDate = selectedDate.add(20, 'days').endOf('day')
          
          const startTime = startDate.utc().toISOString()
          const endTime = endDate.utc().toISOString()
          const zoneId = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata'

          // Fetch booked slots using the booked-slots API
          const bookedSlotsResponse = await getDataWithoutParams(
            `/api/secure/teacher/booked-slots/all-with-patient-info?startTime=${startTime}&endTime=${endTime}&get-patient-details=false`
          )

          if (bookedSlotsResponse?.status === 200 && isCommonResponseSuccessfulV2(bookedSlotsResponse)) {
            const bookedEvents = getDataMapFromCommonResponse(bookedSlotsResponse)?.bookedEvents || []
            // Filter out cancelled slots and only include slots for the current event type
            const activeBookedSlots = bookedEvents.filter(
              (event) => 
                event.status !== 'cancelled' && 
                event.status !== 'CANCELLED' &&
                (event.eventTypeId === currentEventTypeObject.uid || event.eventTypeId === currentEventTypeObject.id)
            )
            setBookedSlots(activeBookedSlots)
          } else {
            setBookedSlots([])
          }
        } catch (error) {
          console.error('Error fetching booked slots:', error)
          setBookedSlots([])
        } finally {
          setBookedSlotsLoading(false)
        }
      }

      fetchBookedSlots()
    }, [selectedDate, showHoldSlotForNextVisit, mentorProfileData?.id, currentEventTypeObject])

    // Removed auto-opening of slot selector - user should manually trigger it

    useImperativeHandle(componentRef, () => ({
      confirmOmissionReason: () => {
        return holdSlotOmissionDialog.show()
      },
    }))

    if (loading) {
      return (
        <Box sx={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
          Loading next visit details...
        </Box>
      )
    }

    // Show booked slot card
    if (bookedSlot) {
      return (
        <>
          <Box
            sx={{
              padding: '20px',
              backgroundColor: bookedSlot?.status === 'booked' 
                ? '#f0fdf4' 
                : '#fffbeb',
              borderRadius: '12px',
              border: `2px solid ${bookedSlot?.status === 'booked' 
                ? '#10b981' 
                : '#f59e0b'}`,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            }}
          >
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <Box sx={{ flex: 1 }}>
                <Chip
                  label={bookedSlot?.status === 'booked' ? 'Slot Booked' : 'Slot Blocked'}
                  color={bookedSlot?.status === 'booked' ? 'success' : 'warning'}
                  size="small"
                  sx={{ 
                    marginBottom: '12px', 
                    fontWeight: 700,
                    fontSize: '12px',
                    backgroundColor: bookedSlot?.status === 'booked' ? '#10b981' : '#f59e0b',
                    color: '#ffffff',
                    '& .MuiChip-label': {
                      color: '#ffffff',
                      fontWeight: 700,
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '20px',
                    fontWeight: 700,
                    color: bookedSlot?.status === 'booked' ? '#065f46' : '#92400e',
                    marginBottom: '4px',
                  }}
                >
                  {(() => {
                    try {
                      // Try multiple possible field names for start time
                      const startTime = bookedSlot.startTime || bookedSlot.startDateTime
                      if (!startTime) {
                        // Fallback to timeSlot if no startTime
                        return bookedSlot.timeSlot || 'Time not available'
                      }
                      
                      // Get clinic location from bookedSlot or currentClinicLocation
                      const clinicLocation = bookedSlot.clinicLocation || currentClinicLocation
                      
                      // Get duration
                      const duration = bookedSlot.eventDuration || 30
                      
                      // Try multiple possible field names for end time
                      const endTime = bookedSlot.endTime || bookedSlot.endDateTime
                      
                      // Format time in clinic timezone
                      const formattedTime = formatTimeSlotInClinicTimezone(
                        startTime,
                        endTime || null,
                        clinicLocation,
                        duration
                      )
                      
                      // Get clinic timezone
                      const clinicTimezone = getClinicTimezone(clinicLocation)
                      
                      // Parse clinic timezone offset
                      const clinicOffsetMatch = clinicTimezone.match(/^([+-])(\d{2}):(\d{2})$/)
                      let clinicOffsetMinutes = 0
                      if (clinicOffsetMatch) {
                        const sign = clinicOffsetMatch[1] === '+' ? 1 : -1
                        const hours = parseInt(clinicOffsetMatch[2], 10)
                        const minutes = parseInt(clinicOffsetMatch[3], 10)
                        clinicOffsetMinutes = sign * (hours * 60 + minutes)
                      }
                      
                      // Get user's local timezone offset
                      const userOffsetMinutes = dayjs().utcOffset()
                      
                      // Format in user's local timezone if different
                      const startUtc = dayjs.utc(startTime)
                      const endUtc = endTime ? dayjs.utc(endTime) : startUtc.add(duration, 'minutes')
                      const startLocal = startUtc.local()
                      const endLocal = endUtc.local()
                      const localTimeStr = `${startLocal.format('h:mm A')} - ${endLocal.format('h:mm A')}`
                      
                      // If timezones are different, show both
                      if (Math.abs(userOffsetMinutes - clinicOffsetMinutes) > 0) {
                        return (
                          <span>
                            {formattedTime.labelWithTz}
                            <span style={{ fontSize: '14px', fontWeight: 500, marginLeft: '8px', color: '#6b7280' }}>
                              ({localTimeStr} your time)
                            </span>
                          </span>
                        )
                      }
                      
                      // Otherwise just show clinic timezone
                      return formattedTime.labelWithTz
                    } catch (error) {
                      console.error('Error formatting time slot:', error)
                      // Fallback to timeSlot if formatting fails
                      return bookedSlot.timeSlot || 'Time not available'
                    }
                  })()}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: bookedSlot?.status === 'booked' ? '#047857' : '#b45309',
                    marginBottom: '8px',
                    fontWeight: 600,
                  }}
                >
                  {(() => {
                    try {
                      // Try multiple possible field names for start time
                      const startTime = bookedSlot.startTime || bookedSlot.startDateTime
                      if (!startTime) return 'Date not available'
                      const date = dayjs(startTime)
                      if (!date.isValid()) return 'Date not available'
                      return `${date.format('dddd, MMMM D, YYYY')} (${getNextVisitDateDifference(date, dayjs(minDate))})`
                    } catch {
                      return 'Date not available'
                    }
                  })()}
                </Typography>
              </Box>
            </Box>

            {/* Details Grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: '12px',
                paddingTop: '16px',
                borderTop: `1px solid ${bookedSlot?.status === 'booked' 
                  ? 'rgba(16, 185, 129, 0.3)' 
                  : 'rgba(251, 191, 36, 0.3)'}`,
              }}
            >
              {bookedSlot?.eventType && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CalendarMonthIcon sx={{ fontSize: '18px', color: bookedSlot?.status === 'booked' ? '#047857' : '#b45309' }} />
                  <Typography sx={{ fontSize: '13px', color: '#374151', fontWeight: 600 }}>Event Type:</Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: bookedSlot?.status === 'booked' ? '#065f46' : '#92400e' }}>
                    {displayNamesOfEventTypes?.[bookedSlot.eventType] || bookedSlot.eventType}
                  </Typography>
                </Box>
              )}

              {(bookedSlot?.mode || bookedSlot?.meetingSrc) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {(() => {
                    // Check mode first, then meetingSrc
                    const mode = bookedSlot?.mode || bookedSlot?.meetingSrc
                    const isOnline = mode === 'aumhum' || mode === 'online' || mode === 'ONLINE'
                    return isOnline ? (
                      <VideocamIcon sx={{ fontSize: '18px', color: '#0284c7' }} />
                    ) : (
                      <LocationOnIcon sx={{ fontSize: '18px', color: '#dc2626' }} />
                    )
                  })()}
                  <Typography sx={{ fontSize: '13px', color: '#374151', fontWeight: 600 }}>Mode:</Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1f2937' }}>
                    {(() => {
                      // Check mode first, then meetingSrc
                      const mode = bookedSlot?.mode || bookedSlot?.meetingSrc
                      return BOOKED_EVENT_MODES?.[mode]?.title || 
                             BOOKED_EVENT_MODES?.[mode?.toLowerCase()]?.title ||
                             (mode === 'aumhum' || mode === 'online' || mode === 'ONLINE' ? 'Online' : 'Offline')
                    })()}
                  </Typography>
                </Box>
              )}

              {bookedSlot?.chamberRoomName && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <LocationOnIcon sx={{ fontSize: '18px', color: '#dc2626' }} />
                  <Typography sx={{ fontSize: '13px', color: '#374151', fontWeight: 600 }}>Location:</Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1f2937' }}>
                    {bookedSlot.chamberRoomName}
                  </Typography>
                </Box>
              )}

              {(bookedSlot?.eventDuration || (bookedSlot?.startTime && bookedSlot?.endTime)) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <AccessTime sx={{ fontSize: '18px', color: bookedSlot?.status === 'booked' ? '#047857' : '#b45309' }} />
                  <Typography sx={{ fontSize: '13px', color: '#374151', fontWeight: 600 }}>Duration:</Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1f2937' }}>
                    {(() => {
                      try {
                        // Prefer eventDuration if available, otherwise calculate from startTime and endTime
                        let durationMinutes = null
                        
                        if (bookedSlot?.eventDuration) {
                          durationMinutes = Number(bookedSlot.eventDuration)
                        } else if (bookedSlot?.startTime && bookedSlot?.endTime) {
                          const start = dayjs(bookedSlot.startTime)
                          const end = dayjs(bookedSlot.endTime)
                          durationMinutes = end.diff(start, 'minute')
                        }
                        
                        if (!durationMinutes || isNaN(durationMinutes)) {
                          return 'N/A'
                        }
                        
                        if (durationMinutes < 60) {
                          return `${durationMinutes} minutes`
                        }
                        const hours = Math.floor(durationMinutes / 60)
                        const minutes = durationMinutes % 60
                        return minutes > 0 ? `${hours}h ${minutes}m` : `${hours} hour${hours > 1 ? 's' : ''}`
                      } catch {
                        return 'N/A'
                      }
                    })()}
                  </Typography>
                </Box>
              )}

              {(bookedSlot?.feePaid || bookedSlot?.eventFee) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography sx={{ fontSize: '13px', color: '#374151', fontWeight: 600 }}>Fee:</Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1f2937' }}>
                    {(() => {
                      // Check both feePaid and eventFee fields
                      const fee = bookedSlot?.feePaid || bookedSlot?.eventFee
                      const currency = bookedSlot?.currency || 'INR'
                      if (!fee) return 'N/A'
                      return `${currency?.toUpperCase() || 'INR'} ${fee}`
                    })()}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: '8px',
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: `1px solid ${bookedSlot?.status === 'booked' 
                  ? 'rgba(16, 185, 129, 0.3)' 
                  : 'rgba(251, 191, 36, 0.3)'}`,
                flexWrap: 'wrap',
                justifyContent: { xs: 'stretch', sm: 'flex-start' },
              }}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon sx={{ fontSize: '16px' }} />}
                onClick={handleChangeSlot}
                disabled={cancelling || rescheduling}
                sx={{
                  borderColor: 'rgba(139, 92, 246, 0.3)',
                  color: '#7c3aed',
                  textTransform: 'none',
                  fontSize: '13px',
                  fontWeight: 600,
                  padding: '6px 14px',
                  minWidth: 'auto',
                  flex: { xs: '1 1 auto', sm: '0 0 auto' },
                  '&:hover': {
                    borderColor: 'rgba(139, 92, 246, 0.5)',
                    backgroundColor: 'rgba(139, 92, 246, 0.08)',
                  },
                  '&:disabled': {
                    opacity: 0.6,
                  },
                }}
              >
                {rescheduling ? 'Rescheduling...' : cancelling ? 'Cancelling...' : bookedSlot?.status === 'booked' ? 'Reschedule' : 'Change Slot'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DeleteIcon sx={{ fontSize: '16px' }} />}
                onClick={handleCancelSlot}
                disabled={cancelling || rescheduling || bookedSlot?.status === 'booked'}
                sx={{
                  borderColor: 'rgba(239, 68, 68, 0.3)',
                  color: '#ef4444',
                  textTransform: 'none',
                  fontSize: '13px',
                  fontWeight: 600,
                  padding: '6px 14px',
                  minWidth: 'auto',
                  flex: { xs: '1 1 auto', sm: '0 0 auto' },
                  '&:hover': {
                    borderColor: 'rgba(239, 68, 68, 0.5)',
                    backgroundColor: 'rgba(239, 68, 68, 0.08)',
                  },
                  '&:disabled': {
                    opacity: 0.6,
                  },
                }}
              >
                {cancelling ? 'Cancelling...' : 'Cancel'}
              </Button>
            </Box>
          </Box>

          {/* Cancel Reason Dialog */}
          <Dialog
            open={cancelDialogOpen}
            onClose={() => {
              if (!cancelling) {
                setCancelDialogOpen(false)
                setCancelReason('')
                setRefundType('none')
                setPendingCancelAction(null)
              }
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: '12px',
                padding: 0,
              },
            }}
          >
            <DialogTitle sx={{ fontSize: '18px', fontWeight: 700, padding: '24px 24px 16px' }}>
              Cancel Booking
            </DialogTitle>
            <DialogContent sx={{ padding: '0 24px' }}>
              <Typography sx={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                A reason has been pre-filled. You can modify it if needed.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter reason for cancellation..."
                disabled={cancelling}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                  marginBottom: '20px',
                }}
              />
              
              {/* Refund Type Selection */}
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'rgba(0, 0, 0, 0.85)', marginBottom: '12px' }}>
                Refund Recommendation
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '8px' }}>
                <Box
                  onClick={() => !cancelling && setRefundType('none')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: cancelling ? 'not-allowed' : 'pointer',
                    padding: '12px',
                    borderRadius: '8px',
                    border: `2px solid ${refundType === 'none' ? 'rgba(67, 56, 202, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
                    background: refundType === 'none' ? 'rgba(67, 56, 202, 0.05)' : 'transparent',
                    transition: 'all 0.2s ease',
                    opacity: cancelling ? 0.6 : 1,
                  }}
                >
                  <input
                    type="radio"
                    name="refundType"
                    value="none"
                    checked={refundType === 'none'}
                    onChange={(e) => setRefundType(e.target.value)}
                    disabled={cancelling}
                    style={{ cursor: cancelling ? 'not-allowed' : 'pointer' }}
                  />
                  <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'rgba(0, 0, 0, 0.85)' }}>
                      No Refund
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.6)', marginTop: '2px' }}>
                      Patient will not receive a refund
                    </Typography>
                  </Box>
                </Box>
                <Box
                  onClick={() => !cancelling && setRefundType('full')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: cancelling ? 'not-allowed' : 'pointer',
                    padding: '12px',
                    borderRadius: '8px',
                    border: `2px solid ${refundType === 'full' ? 'rgba(67, 56, 202, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
                    background: refundType === 'full' ? 'rgba(67, 56, 202, 0.05)' : 'transparent',
                    transition: 'all 0.2s ease',
                    opacity: cancelling ? 0.6 : 1,
                  }}
                >
                  <input
                    type="radio"
                    name="refundType"
                    value="full"
                    checked={refundType === 'full'}
                    onChange={(e) => setRefundType(e.target.value)}
                    disabled={cancelling}
                    style={{ cursor: cancelling ? 'not-allowed' : 'pointer' }}
                  />
                  <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'rgba(0, 0, 0, 0.85)' }}>
                      Full Refund
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.6)', marginTop: '2px' }}>
                      Recommend full refund to the patient
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px 24px', gap: '8px' }}>
              <Button
                onClick={() => {
                  if (!cancelling) {
                    setCancelDialogOpen(false)
                    setCancelReason('')
                    setRefundType('none')
                    setPendingCancelAction(null)
                  }
                }}
                disabled={cancelling}
                sx={{
                  textTransform: 'none',
                  color: '#666',
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmCancel}
                disabled={cancelling}
                variant="contained"
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#ef4444',
                  '&:hover': {
                    backgroundColor: '#dc2626',
                  },
                }}
              >
                {cancelling ? 'Cancelling...' : 'Confirm Cancel'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Slot Selector Modal */}
          <NextVisitSlotSelector
            open={slotSelectorOpen}
            setOpen={setSlotSelectorOpen}
            leaveRoom={leaveRoom}
            bookingDate={dayjsMinDateInLocalTimezone}
            onClose={(willNotResetNextVisit = false) => {
              setSlotSelectorOpen(false)
              if (!willNotResetNextVisit && !bookedSlot) {
                resetNextVisit()
              }
            }}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedNextVisitDate}
            onSlotSelect={handleSlotSelection}
            selectedItem={currentClinicLocation}
            roomId={roomId}
            mentorProfile={mentorProfileData}
            currentEventTypeObject={currentEventTypeObject}
            currentFee={currentFee}
            selectedPatient={currentPatient}
            refreshData={refreshCurrentBookedEventInfoData}
            currentBookedEventId={currentBookedEventId}
            currentCurrency={currentCurrency}
            saveReport={saveReport}
            nextVisitHoldSlotOmissionReason={nextVisitHoldSlotOmissionReason}
            setNextVisitHoldSlotOmissionReason={setNextVisitHoldSlotOmissionReason}
            isMarkingAsCompleted={isMarkingAsCompleted}
            holdSlotOmissionDialog={holdSlotOmissionDialog}
          />
        </>
      )
    }

    // Show date picker and input when no slot is held
    return (
      <>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', overflow: 'hidden' }}>
          {/* Date Input Section - Combined Input and Pills with Date Picker on Same Line */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
            {/* Helper text */}
            <Typography
              sx={{
                fontSize: '13px',
                color: '#4b5563',
                fontWeight: 600,
                marginBottom: '6px',
              }}
            >
              Select date by entering days/weeks/months or choose a specific date:
            </Typography>
            
            {/* Combined Input/Pills and Date Picker on Same Line */}
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'stretch', flexWrap: { xs: 'wrap', md: 'nowrap' }, width: '100%', overflow: 'hidden' }}>
              {/* Number Input with Pills */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: '0px', 
                  alignItems: 'stretch', 
                  flex: { xs: '1 1 100%', md: '0 1 auto' },
                  minWidth: { xs: '100%', md: '240px' },
                  maxWidth: { xs: '100%', md: '300px' },
                  width: { xs: '100%', md: 'auto' },
                  border: '1px solid rgba(139, 92, 246, 0.25)',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 2px 4px rgba(139, 92, 246, 0.1)',
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                  '&:hover': {
                    borderColor: 'rgba(139, 92, 246, 0.4)',
                    boxShadow: '0 2px 8px rgba(139, 92, 246, 0.15)',
                  },
                  '&:focus-within': {
                    borderColor: 'rgba(139, 92, 246, 0.6)',
                    boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.15)',
                  },
                }}
              >
              {/* Number Input */}
              <Box sx={{ flex: '0 0 auto', minWidth: '90px' }}>
                <ModernNumberInput
                  type="number"
                  placeholder="0"
                  value={nextVisitInputValue ?? ''}
                  onChange={handleInputChange}
                  onBlur={() => setInputTouched(true)}
                  onFocus={() => setInputTouched(true)}
                  error={!!inputError && inputTouched}
                  helperText={inputError && inputTouched ? inputError : ''}
                  size="small"
                  InputProps={{
                    inputProps: {
                      min: 1,
                      max: nextVisitInputType === 'days' ? 365 : nextVisitInputType === 'weeks' ? 52 : 12,
                      step: 1,
                      style: {
                        textAlign: 'center',
                        padding: '8px 12px',
                        fontSize: '15px',
                        fontWeight: 600,
                      },
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      border: 'none',
                      borderRadius: 0,
                      boxShadow: 'none',
                      '&:hover': {
                        border: 'none',
                        boxShadow: 'none',
                      },
                      '&.Mui-focused': {
                        border: 'none',
                        boxShadow: 'none',
                      },
                    },
                    '& .MuiInputBase-input': {
                      padding: '10px 14px',
                      fontSize: '15px',
                      fontWeight: 600,
                    },
                    '& .MuiFormHelperText-root': {
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      marginTop: '4px',
                      fontSize: '11px',
                      color: '#ef4444',
                      display: inputError && inputTouched ? 'block' : 'none',
                      lineHeight: 1.3,
                    },
                  }}
                />
              </Box>
              
              {/* Divider */}
              <Box
                sx={{
                  width: '1px',
                  backgroundColor: 'rgba(139, 92, 246, 0.2)',
                  alignSelf: 'stretch',
                }}
              />
              
              {/* Pills Container */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  flex: '1 1 auto',
                  minWidth: { xs: '100%', sm: 'auto' },
                  borderLeft: { xs: 'none', sm: '1px solid rgba(139, 92, 246, 0.15)' },
                  borderTop: { xs: '1px solid rgba(139, 92, 246, 0.15)', sm: 'none' },
                }}
              >
                {['days', 'weeks', 'months'].map((type, index) => (
                  <Box
                    key={type}
                    onClick={() => {
                      setNextVisitInputType(type)
                      if (!nextVisitInputValue || nextVisitInputValue === '') {
                        setInputError('')
                        setInputTouched(false)
                      }
                    }}
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '10px 12px',
                      cursor: 'pointer',
                      backgroundColor: nextVisitInputType === type 
                        ? 'rgba(139, 92, 246, 0.15)' 
                        : 'transparent',
                      color: nextVisitInputType === type 
                        ? '#6b21a8' 
                        : '#6b7280',
                      fontWeight: nextVisitInputType === type ? 700 : 500,
                      fontSize: '13px',
                      textTransform: 'capitalize',
                      transition: 'all 0.2s ease',
                      borderRight: index < 2 ? '1px solid rgba(139, 92, 246, 0.15)' : 'none',
                      '&:hover': {
                        backgroundColor: nextVisitInputType === type 
                          ? 'rgba(139, 92, 246, 0.2)' 
                          : 'rgba(139, 92, 246, 0.05)',
                        color: '#6b21a8',
                      },
                      '&:active': {
                        backgroundColor: 'rgba(139, 92, 246, 0.25)',
                      },
                    }}
                  >
                    {type}
                  </Box>
                ))}
              </Box>
            </Box>
            
            {/* Error message below the combined component */}
            {inputError && inputTouched && (
              <Typography
                sx={{
                  fontSize: '11px',
                  color: '#ef4444',
                  marginTop: '4px',
                  marginLeft: '4px',
                  lineHeight: 1.3,
                }}
              >
                {inputError}
              </Typography>
            )}
              
              {/* Divider with "OR" */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flex: { xs: '1 1 100%', md: '0 0 auto' },
                  justifyContent: 'center',
                  width: { xs: '100%', md: 'auto' },
                  order: { xs: 2, md: 0 },
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: '1px',
                    backgroundColor: 'rgba(139, 92, 246, 0.2)',
                    display: { xs: 'none', md: 'block' },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '13px',
                    color: '#6b7280',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    padding: '0 8px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '4px',
                  }}
                >
                  OR
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    height: '1px',
                    backgroundColor: 'rgba(139, 92, 246, 0.2)',
                    display: { xs: 'none', md: 'block' },
                  }}
                />
              </Box>
              
              {/* Date Picker */}
              <Box sx={{ 
                flex: { xs: '1 1 100%', md: '1 1 auto' }, 
                minWidth: { xs: '100%', md: '0' },
                maxWidth: { xs: '100%', md: '100%' },
                order: { xs: 3, md: 0 },
                width: { xs: '100%', md: 'auto' },
                overflow: 'hidden',
              }}>
                <DayjsDatePicker
                  date={datePickerButtonText ? selectedDate : null}
                  offDayWeekdays={offDayWeekdays}
                  calendarProps={{
                    minDate: dayjs().toDate(),
                    maxDate: maxDate,
                    minDetail: 'decade',
                    showNeighbouringMonth: false,
                    locale: sessionStorage.getItem('languageCode') || 'en',
                  }}
                  format="DD MMM YYYY, dddd"
                  buttonTextOverride={datePickerButtonText}
                  buttonProps={{
                    variant: 'outlined',
                    size: 'medium',
                    startIcon: <CalendarMonthIcon />,
                    sx: {
                      background: datePickerButtonText ? '#ffffff' : 'rgba(139, 92, 246, 0.05)',
                      textTransform: 'none',
                      border: datePickerButtonText 
                        ? '1px solid rgba(139, 92, 246, 0.25)' 
                        : '1px dashed rgba(139, 92, 246, 0.4)',
                      color: '#6b21a8',
                      borderRadius: '10px',
                      padding: '10px 16px',
                      width: '100%',
                      maxWidth: '100%',
                      boxShadow: datePickerButtonText 
                        ? '0 2px 4px rgba(139, 92, 246, 0.1)' 
                        : 'none',
                      transition: 'all 0.2s ease',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        borderColor: 'rgba(139, 92, 246, 0.5)',
                        backgroundColor: 'rgba(139, 92, 246, 0.08)',
                        boxShadow: '0 2px 8px rgba(139, 92, 246, 0.15)',
                      },
                    },
                  }}
                  extraHandlingAfterOnClick={() => {
                    if ((nextVisitInputValue === null || nextVisitInputValue === undefined)) {
                      setNextVisitInputValue(7)
                    }
                  }}
                  onChange={setSelectedNextVisitDate}
                  placeholder="Pick Date"
                />
              </Box>
            </Box>
          </Box>

          {/* Hold Slot Button - Show if enabled and date is set */}
          {holdSlotButtonText && (
            <Box>
              <Button
                variant="contained"
                startIcon={<AccessTime />}
                onClick={() => setSlotSelectorOpen(true)}
                sx={{
                  backgroundColor: '#7c3aed',
                  color: '#ffffff',
                  textTransform: 'none',
                  borderRadius: '10px',
                  padding: '12px 24px',
                  fontWeight: 600,
                  boxShadow: '0 2px 8px rgba(124, 58, 237, 0.3)',
                  '&:hover': {
                    backgroundColor: '#6b21a8',
                    boxShadow: '0 4px 12px rgba(124, 58, 237, 0.4)',
                  },
                }}
              >
                {holdSlotButtonText}
              </Button>
            </Box>
          )}
        </Box>

        {/* Slot Selector Modal */}
        <NextVisitSlotSelector
          open={slotSelectorOpen}
          setOpen={setSlotSelectorOpen}
          leaveRoom={leaveRoom}
          bookingDate={dayjsMinDateInLocalTimezone}
          onClose={(willNotResetNextVisit = false) => {
            setSlotSelectorOpen(false)
            if (!willNotResetNextVisit) {
              resetNextVisit()
            }
          }}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedNextVisitDate}
          onSlotSelect={handleSlotSelection}
          selectedItem={currentClinicLocation}
          roomId={roomId}
          mentorProfile={mentorProfileData}
          currentEventTypeObject={currentEventTypeObject}
          currentFee={currentFee}
          selectedPatient={currentPatient}
          refreshData={refreshCurrentBookedEventInfoData}
          currentBookedEventId={currentBookedEventId}
          currentCurrency={currentCurrency}
          saveReport={saveReport}
          nextVisitHoldSlotOmissionReason={nextVisitHoldSlotOmissionReason}
          setNextVisitHoldSlotOmissionReason={setNextVisitHoldSlotOmissionReason}
          isMarkingAsCompleted={isMarkingAsCompleted}
          holdSlotOmissionDialog={holdSlotOmissionDialog}
          clinicLocation={currentClinicLocation}
          bookedSlots={bookedSlots}
          bookedSlotsLoading={bookedSlotsLoading}
        />
      </>
    )
  },
)

EMRNextVisitComponentModernized.displayName = 'EMRNextVisitComponentModernized'

export default EMRNextVisitComponentModernized
